import { useEffect, useMemo, useRef, useState } from "react";
import { academyArticles } from "../data/academyArticles";
import heroImage from "../source/airdrop/5b5c6083d5384cbcc5839503d28c9ee4cefb953a.png";
import { IS_PRERENDER } from "../utils/prerender";

const getSlugFromPath = (path) => {
  if (!path) {
    return "";
  }
  const [, slugPart = ""] = path.split("/academy/");
  return slugPart.replace(/^\/+|\/+$/g, "");
};

const buildSectionId = (value) =>
  value
    ? value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "")
    : "";

const renderParagraphText = (text) => {
  if (!text || typeof text !== "string") {
    return text;
  }
  const sentences = text.split(/(?<=[.!?])\s+(?=[A-Z])/);
  if (sentences.length === 1) {
    return text;
  }
  return sentences.map((sentence, index) => (
    <span key={`sentence-${index}`}>
      {sentence}
      {index < sentences.length - 1 && (
        <>
          <br />
          <br />
        </>
      )}
    </span>
  ));
};

const calloutIcons = {
  warn: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 3.8l8.2 15H3.8l8.2-15z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <line
        x1="12"
        y1="9"
        x2="12"
        y2="13.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <circle cx="12" cy="17.2" r="1" fill="currentColor" />
    </svg>
  ),
};

function AcademyDetailPage({ route }) {
  const isPrerender = IS_PRERENDER;
  const tocListRef = useRef(null);
  const contentRef = useRef(null);
  const pendingScrollRef = useRef(null);
  const imageLoading = isPrerender ? undefined : "lazy";
  const [tocHeight, setTocHeight] = useState(0);
  const [thumbStyle, setThumbStyle] = useState({
    height: "32px",
    transform: "translateY(0)",
  });
  const [pendingScrollId, setPendingScrollId] = useState(null);

  const slug = getSlugFromPath(route || window.location.pathname);
  const article = useMemo(() => {
    const stateArticle = window.history.state?.academyArticle;
    if (stateArticle?.slug && stateArticle.slug === slug) {
      return stateArticle;
    }
    return academyArticles.find((item) => item.slug === slug);
  }, [slug]);

  const heroPoster = article?.heroImage || heroImage;
  const sections = useMemo(() => article?.sections ?? [], [article?.sections]);

  const tocSections = useMemo(
    () =>
      sections.map((section, index) => {
        const title = section.title;
        const id = buildSectionId(title);
        const displayIndex = String(index + 1).padStart(2, "0");
        return {
          id,
          title,
          displayTitle: `${displayIndex}. ${title}`,
        };
      }),
    [sections]
  );

  const [activeTocId, setActiveTocId] = useState(tocSections[0]?.id ?? null);
  const activeTitle =
    tocSections.find(({ id }) => id === activeTocId)?.displayTitle ??
    tocSections[0]?.displayTitle ??
    "Overview";

  useEffect(() => {
    setActiveTocId(tocSections[0]?.id ?? null);
    setPendingScrollId(null);
    if (!tocSections.length) {
      setTocHeight(0);
      setThumbStyle({ height: "32px", transform: "translateY(0)" });
    }
  }, [tocSections]);

  useEffect(() => {
    pendingScrollRef.current = pendingScrollId;
  }, [pendingScrollId]);

  useEffect(() => {
    const list = tocListRef.current;
    if (!list || list.scrollHeight <= list.clientHeight + 1) {
      return;
    }
    const activeButton = list.querySelector('button[aria-current="true"]');
    if (!activeButton) {
      return;
    }
    const listRect = list.getBoundingClientRect();
    const buttonRect = activeButton.getBoundingClientRect();
    const padding = 8;
    if (buttonRect.top < listRect.top + padding) {
      list.scrollTop -= listRect.top - buttonRect.top + padding;
    } else if (buttonRect.bottom > listRect.bottom - padding) {
      list.scrollTop += buttonRect.bottom - listRect.bottom + padding;
    }
  }, [activeTocId]);

  const updateThumbForIndex = (index) => {
    if (!tocListRef.current) {
      return;
    }
    const listItems = tocListRef.current.querySelectorAll("li");
    if (!listItems.length) {
      return;
    }
    const listRect = tocListRef.current.getBoundingClientRect();
    const listHeight = listRect.height;
    if (listHeight <= 0) {
      return;
    }
    setTocHeight(listHeight);

    const safeIndex = Math.min(listItems.length - 1, Math.max(0, index));
    const itemRect = listItems[safeIndex].getBoundingClientRect();
    const itemHeight = itemRect.height;
    const minThumb = 32;
    const maxThumb = listHeight;
    const thumbHeight = Math.min(maxThumb, Math.max(minThumb, itemHeight));
    const maxOffset = Math.max(0, listHeight - thumbHeight);
    const maxIndex = Math.max(1, listItems.length - 1);
    const progress = maxIndex === 0 ? 0 : safeIndex / maxIndex;
    const thumbOffset = progress * maxOffset;
    setThumbStyle({
      height: `${thumbHeight}px`,
      transform: `translateY(${thumbOffset}px)`,
    });
  };

  const handleScrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) {
      return;
    }
    if (activeTocId === id) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    setActiveTocId(id);
    setPendingScrollId(id);
    const targetIndex = tocSections.findIndex((section) => section.id === id);
    if (targetIndex >= 0) {
      updateThumbForIndex(targetIndex);
    }
  };

  useEffect(() => {
    const pageTitle = article?.title
      ? `${article.title} | Coinporate Academy`
      : "Coinporate Academy";
    document.title = pageTitle;
    const metaDescription = document.querySelector('meta[name="description"]');
    const description =
      "Read Coinporate Academy guides on staking, fees, wallets, and tokenomics. Learn practical steps for on-chain participation with clear examples and safety tips.";
    if (metaDescription) {
      metaDescription.setAttribute("content", description);
    } else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content = description;
      document.getElementsByTagName("head")[0].appendChild(meta);
    }
  }, [article]);

  useEffect(() => {
    if (!pendingScrollId) {
      return;
    }
    const el = document.getElementById(pendingScrollId);
    if (!el) {
      setPendingScrollId(null);
      return;
    }
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [pendingScrollId]);

  useEffect(() => {
    if (!tocSections.length) {
      return;
    }
    const updateTocMetrics = () => {
      if (!tocListRef.current || !contentRef.current) {
        return;
      }
      const scrollY = window.scrollY;
      const viewport = window.innerHeight;
      const contentRect = contentRef.current.getBoundingClientRect();
      const contentTop = contentRect.top + scrollY;
      const contentHeight = contentRef.current.offsetHeight;
      const contentBottom = contentTop + contentHeight;
      const isAtBottom = scrollY + viewport >= contentBottom - 1;
      const pendingId = pendingScrollRef.current;

      const anchor = scrollY + viewport * 0.25;
      let nextActiveId = tocSections[0]?.id ?? null;
      if (pendingId) {
        nextActiveId = pendingId;
        const targetEl = document.getElementById(pendingId);
        if (!targetEl) {
          setPendingScrollId(null);
        } else {
          const targetTop = targetEl.getBoundingClientRect().top + scrollY;
          const reachedTarget =
            Math.abs(scrollY - targetTop) <= 2 || isAtBottom;
          if (reachedTarget) {
            setPendingScrollId(null);
          }
        }
      } else {
        tocSections.forEach(({ id }) => {
          const sectionEl = document.getElementById(id);
          if (!sectionEl) {
            return;
          }
          const sectionTop = sectionEl.getBoundingClientRect().top + scrollY;
          if (anchor >= sectionTop) {
            nextActiveId = id;
          }
        });
        if (isAtBottom && tocSections.length) {
          nextActiveId = tocSections[tocSections.length - 1].id;
        }
      }
      if (nextActiveId) {
        setActiveTocId((prev) => (prev === nextActiveId ? prev : nextActiveId));
      }
      const activeIndex = Math.max(
        0,
        tocSections.findIndex(({ id }) => id === nextActiveId)
      );
      updateThumbForIndex(activeIndex);
    };

    updateTocMetrics();
    window.addEventListener("scroll", updateTocMetrics, { passive: true });
    window.addEventListener("resize", updateTocMetrics);
    return () => {
      window.removeEventListener("scroll", updateTocMetrics);
      window.removeEventListener("resize", updateTocMetrics);
    };
  }, [tocSections]);

  return (
    <main className="academy-detail">
      <section className="academy-detail-hero" aria-hidden="true">
        <div className="academy-detail-hero__video-wrap">
          <img
            className="academy-detail-hero__image"
            src={heroPoster}
            alt=""
            loading="eager"
          />
          <div className="academy-detail-hero__veil" />
        </div>
        <div className="academy-detail-hero__title">
          {article?.title || "Academy"}
        </div>
      </section>
      <h1 className="visually-hidden">
        {article?.title || "Coinporate Academy"}
      </h1>
      <section className="academy-detail-body">
        <div className="academy-detail-body__inner">
          <aside className="academy-detail-toc">
            <h2 className="visually-hidden">Table of contents</h2>
            <h3 className="academy-detail-toc__label">FIND TOPIC</h3>
            <div className="academy-detail-toc__current" aria-live="polite">
              <span className="academy-detail-toc__current-label">
                Current section
              </span>
              <span className="academy-detail-toc__current-title">
                {activeTitle}
              </span>
            </div>
            <div className="academy-detail-toc__body">
              <div
                className="academy-detail-toc__scroll"
                style={{ height: tocHeight ? `${tocHeight}px` : "135%" }}
              >
                <div className="academy-detail-toc__track" />
                <div className="academy-detail-toc__thumb" style={thumbStyle} />
              </div>
              <ul ref={tocListRef}>
                {tocSections.map(({ id, title, displayTitle }) => {
                  const isActive = activeTocId === id;
                  return (
                    <li key={id || title}>
                      <button
                        type="button"
                        className={`academy-detail-toc__button ${
                          isActive ? "academy-detail-toc__button--active" : ""
                        }`.trim()}
                        aria-current={isActive ? "true" : undefined}
                        onClick={() => handleScrollTo(id)}
                      >
                        {displayTitle || title}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </aside>
          <div className="academy-detail-content" ref={contentRef}>
            {sections.length ? (
              sections.map((section, index) => {
                const sectionId = buildSectionId(section.title);
                const isActive = activeTocId === sectionId;
                const titleClassName = `academy-detail-block__title ${
                  isActive
                    ? "academy-detail-block__title--hero"
                    : "academy-detail-block__title--mono"
                }`;
                const titleContent = section.displayTitle || section.title;
                const paragraphs = section.paragraphs || [];
                const listItems = section.list || [];
                const table =
                  section.table ||
                  (section.type === "table"
                    ? {
                        columns: section.columns || [],
                        rows: section.rows || [],
                      }
                    : null);
                const listVariant = section.listVariant;
                const callout = section.callout;
                const faqItems = section.items || [];
                const tailParagraphs = section.tailParagraphs || [];
                const mediaItems = section.media || [];
                const ListTag = section.ordered ? "ol" : "ul";
                const listClassName = `academy-detail-block__list ${
                  section.ordered ? "academy-detail-block__list--ordered" : ""
                } ${listVariant ? `academy-detail-block__list--${listVariant}` : ""}`.trim();

                return (
                  <article
                    key={`${section.title}-${index}`}
                    className="academy-detail-block"
                    id={sectionId}
                  >
                    <h2 className={titleClassName}>{titleContent}</h2>
                    <div className="academy-detail-block__content">
                      {paragraphs.map((paragraph, paragraphIndex) => {
                        const isObject =
                          paragraph &&
                          typeof paragraph === "object" &&
                          !Array.isArray(paragraph) &&
                          ("text" in paragraph || "content" in paragraph);
                        const paragraphContent = isObject
                          ? paragraph.content ?? paragraph.text
                          : paragraph;
                        const paragraphVariant = isObject
                          ? paragraph.variant
                          : null;
                        const paragraphClassName = `academy-detail-block__paragraph ${
                          paragraphVariant
                            ? `academy-detail-block__paragraph--${paragraphVariant}`
                            : ""
                        }`.trim();
                        if (
                          paragraphVariant === "block" &&
                          paragraphContent &&
                          typeof paragraphContent !== "string"
                        ) {
                          return (
                            <div
                              key={`paragraph-${index}-${paragraphIndex}`}
                              className="academy-detail-block__block"
                            >
                              {paragraphContent}
                            </div>
                          );
                        }
                        return (
                          <p
                            key={`paragraph-${index}-${paragraphIndex}`}
                            className={paragraphClassName}
                          >
                            {typeof paragraphContent === "string"
                              ? renderParagraphText(paragraphContent)
                              : paragraphContent}
                          </p>
                        );
                      })}
                      {listItems.length > 0 && (
                        <div className="academy-detail-block__list-group">
                          {section.listTitle && (
                            <p className="academy-detail-block__list-title">
                              {section.listTitle}
                            </p>
                          )}
                          <ListTag className={listClassName}>
                            {listItems.map((item, itemIndex) => {
                              const isObject =
                                item &&
                                typeof item === "object" &&
                                !Array.isArray(item);
                              const itemContent = isObject
                                ? item.content ?? item.text
                                : item;
                              const itemTitle = isObject ? item.title : null;
                              const itemParagraphs = isObject
                                ? item.paragraphs || []
                                : [];
                              const itemDetails = isObject
                                ? item.details || []
                                : [];
                              const detailItems = Array.isArray(itemDetails)
                                ? itemDetails
                                : [];
                              const hasStructuredContent =
                                Boolean(itemTitle) || itemParagraphs.length > 0;
                              const itemMedia = isObject ? item.media : null;
                              const mediaItems = Array.isArray(itemMedia)
                                ? itemMedia
                                : itemMedia
                                  ? [itemMedia]
                                  : [];
                              return (
                                <li
                                  key={`item-${index}-${itemIndex}`}
                                  className={
                                    [
                                      "academy-detail-block__list-item",
                                      mediaItems.length
                                        ? "academy-detail-block__list-item--media"
                                        : "",
                                      hasStructuredContent
                                        ? "academy-detail-block__list-item--stacked"
                                        : "",
                                    ]
                                      .filter(Boolean)
                                      .join(" ")
                                  }
                                >
                                  {hasStructuredContent ? (
                                    <div className="academy-detail-block__list-item-body">
                                      {itemTitle && (
                                        <div
                                          className="academy-detail-block__list-item-title"
                                          data-step
                                        >
                                          {itemTitle}
                                        </div>
                                      )}
                                      {itemParagraphs.map(
                                        (paragraph, paragraphIndex) => (
                                          <p
                                            key={`list-paragraph-${index}-${itemIndex}-${paragraphIndex}`}
                                            className="academy-detail-block__list-item-paragraph"
                                          >
                                            {renderParagraphText(paragraph)}
                                          </p>
                                        )
                                      )}
                                      {!itemParagraphs.length && itemContent ? (
                                        <div className="academy-detail-block__list-item-text">
                                          {itemContent}
                                        </div>
                                      ) : null}
                                      {detailItems.length ? (
                                        <ul className="academy-detail-block__step-details">
                                          {detailItems.map(
                                            (detail, detailIndex) => {
                                              const detailContent =
                                                detail &&
                                                typeof detail === "object" &&
                                                !Array.isArray(detail)
                                                  ? detail.content ??
                                                    detail.text ??
                                                    null
                                                  : detail;
                                              const detailMedia =
                                                detail && typeof detail === "object"
                                                  ? detail.media
                                                  : null;
                                              const detailVariant =
                                                detail && typeof detail === "object"
                                                  ? detail.variant
                                                  : null;
                                              const detailMediaItems =
                                                Array.isArray(detailMedia)
                                                  ? detailMedia
                                                  : detailMedia
                                                    ? [detailMedia]
                                                    : [];
                                              const isMediaOnly =
                                                detailVariant === "media";
                                              const isBlock =
                                                detailVariant === "block";
                                              return (
                                                <li
                                                  key={`detail-${index}-${itemIndex}-${detailIndex}`}
                                                  className={`academy-detail-block__step-detail ${
                                                    isMediaOnly
                                                      ? "academy-detail-block__step-detail--media"
                                                      : ""
                                                  } ${
                                                    isBlock
                                                      ? "academy-detail-block__step-detail--block"
                                                      : ""
                                                  }`.trim()}
                                                >
                                                  {detailContent && !isMediaOnly ? (
                                                    isBlock ? (
                                                      typeof detailContent ===
                                                      "string" ? (
                                                        <p className="academy-detail-block__step-paragraph">
                                                          {renderParagraphText(
                                                            detailContent
                                                          )}
                                                        </p>
                                                      ) : (
                                                        detailContent
                                                      )
                                                    ) : (
                                                      <span className="academy-detail-block__step-text">
                                                        {typeof detailContent ===
                                                        "string"
                                                          ? renderParagraphText(
                                                              detailContent
                                                            )
                                                          : detailContent}
                                                      </span>
                                                    )
                                                  ) : null}
                                                  {detailMediaItems.map(
                                                    (mediaItem, mediaIndex) => {
                                                      if (!mediaItem?.src) {
                                                        return null;
                                                      }
                                                      const mediaType =
                                                        mediaItem?.type ||
                                                        "image";
                                                      return (
                                                        <div
                                                          key={`detail-media-${index}-${itemIndex}-${detailIndex}-${mediaIndex}`}
                                                          className="academy-detail-block__image academy-detail-block__image--step"
                                                        >
                                                          {mediaType ===
                                                          "video" ? (
                                                            isPrerender ? null : (
                                                              <iframe
                                                                src={mediaItem.src}
                                                                title={
                                                                  mediaItem.title ||
                                                                  "Academy video"
                                                                }
                                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                                allowFullScreen
                                                                loading="lazy"
                                                              />
                                                            )
                                                          ) : (
                                                            <img
                                                              src={mediaItem.src}
                                                              alt={mediaItem.alt || ""}
                                                              loading={imageLoading}
                                                            />
                                                          )}
                                                        </div>
                                                      );
                                                    }
                                                  )}
                                                </li>
                                              );
                                            }
                                          )}
                                        </ul>
                                      ) : null}
                                    </div>
                                  ) : (
                                    itemContent
                                  )}
                                  {mediaItems.map((mediaItem, mediaIndex) => {
                                    if (!mediaItem?.src) {
                                      return null;
                                    }
                                    const mediaType =
                                      mediaItem?.type || "image";
                                    return (
                                      <div
                                        key={`list-media-${index}-${itemIndex}-${mediaIndex}`}
                                        className="academy-detail-block__image"
                                      >
                                        {mediaType === "video" ? (
                                          isPrerender ? null : (
                                            <iframe
                                              src={mediaItem.src}
                                              title={
                                                mediaItem.title ||
                                                "Academy video"
                                              }
                                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                              allowFullScreen
                                              loading="lazy"
                                            />
                                          )
                                        ) : (
                                          <img
                                            src={mediaItem.src}
                                            alt={mediaItem.alt || ""}
                                            loading={imageLoading}
                                          />
                                        )}
                                      </div>
                                    );
                                  })}
                                </li>
                              );
                            })}
                          </ListTag>
                        </div>
                      )}
                      {table && table.columns?.length && table.rows?.length ? (
                        <div className="whitepaper-block__table-wrap">
                          <table className="whitepaper-block__table">
                            <thead>
                              <tr>
                                {table.columns.map((column) => (
                                  <th
                                    key={`${section.title}-column-${column}`}
                                  >
                                    {column}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {table.rows.map((row, rowIndex) => (
                                <tr key={`${section.title}-row-${rowIndex}`}>
                                  {row.map((cell, cellIndex) => {
                                    const cellLabel =
                                      table.columns[cellIndex] || "";
                                    return (
                                      <td
                                        key={`${section.title}-cell-${rowIndex}-${cellIndex}`}
                                        data-label={cellLabel}
                                      >
                                        {cell}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : null}
                      {faqItems.length ? (
                        <div className="faq__list faq__list--static">
                          {faqItems.map((item, faqIndex) => {
                            const number = item.number || String(faqIndex + 1);
                            const answer = item.answer;
                            const answerParts = Array.isArray(answer)
                              ? answer
                              : answer
                                ? [answer]
                                : [];
                            return (
                              <div
                                key={`${number}-${item.question}`}
                                className="faq__item faq__item--open faq__item--static"
                              >
                                <div className="faq__item-toggle">
                                  <span className="faq__item-number">
                                    {number}.
                                  </span>
                                  <span className="faq__item-title">
                                    {item.question}
                                  </span>
                                  <span
                                    className="faq__item-icon"
                                    aria-hidden="true"
                                  />
                                </div>
                                <div className="faq__item-panel">
                                  <div className="faq__item-body">
                                    {answerParts.map((part, partIndex) => (
                                      <p
                                        key={`faq-answer-${faqIndex}-${partIndex}`}
                                      >
                                        {part}
                                      </p>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : null}
                      {tailParagraphs.map((paragraph, paragraphIndex) => {
                        const isObject =
                          paragraph &&
                          typeof paragraph === "object" &&
                          !Array.isArray(paragraph) &&
                          ("text" in paragraph || "content" in paragraph);
                        const paragraphContent = isObject
                          ? paragraph.content ?? paragraph.text
                          : paragraph;
                        const paragraphVariant = isObject
                          ? paragraph.variant
                          : null;
                        const paragraphClassName = `academy-detail-block__paragraph ${
                          paragraphVariant
                            ? `academy-detail-block__paragraph--${paragraphVariant}`
                            : ""
                        }`.trim();
                        if (
                          paragraphVariant === "block" &&
                          paragraphContent &&
                          typeof paragraphContent !== "string"
                        ) {
                          return (
                            <div
                              key={`tail-paragraph-${index}-${paragraphIndex}`}
                              className="academy-detail-block__block"
                            >
                              {paragraphContent}
                            </div>
                          );
                        }
                        return (
                          <p
                            key={`tail-paragraph-${index}-${paragraphIndex}`}
                            className={paragraphClassName}
                          >
                            {typeof paragraphContent === "string"
                              ? renderParagraphText(paragraphContent)
                              : paragraphContent}
                          </p>
                        );
                      })}
                      {callout ? (
                        <div
                          className={`academy-detail-callout academy-detail-callout--${callout.variant} ${
                            callout.align === "center"
                              ? "academy-detail-callout--center"
                              : ""
                          }`.trim()}
                        >
                          {callout.icon ? (
                            <span className="academy-detail-callout__icon">
                              {calloutIcons[callout.icon]}
                            </span>
                          ) : null}
                          <div className="academy-detail-callout__text">
                            {callout.content}
                          </div>
                        </div>
                      ) : null}
                      {mediaItems.map((item, mediaIndex) => {
                        const mediaType = item?.type || "image";
                        const mediaKey = `media-${index}-${mediaIndex}`;
                        if (!item?.src) {
                          return null;
                        }
                        return (
                          <div
                            className="academy-detail-block__image"
                            key={mediaKey}
                          >
                            {mediaType === "video" ? (
                              isPrerender ? null : (
                                <iframe
                                  src={item.src}
                                  title={item.title || "Academy video"}
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                  loading="lazy"
                                />
                              )
                            ) : (
                              <img
                                src={item.src}
                                alt={item.alt || ""}
                                loading={imageLoading}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {index !== sections.length - 1 && (
                      <div className="academy-detail-block__divider" />
                    )}
                  </article>
                );
              })
            ) : (
              <article className="academy-detail-block">
                <h2 className="academy-detail-block__title academy-detail-block__title--mono">
                  No content yet
                </h2>
                <div className="academy-detail-block__content">
                  <p className="academy-detail-block__paragraph">
                    This academy article does not have any sections yet.
                  </p>
                </div>
              </article>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}

export default AcademyDetailPage;
